import { Controller } from "stimulus";

export default class extends Controller {
    static targets = [
      "pepCheckbox",
      "pepSection",
      "pepField",
      "boCheckbox",
      "boSection",
      "boField",
      "submitButton"
    ]

    connect() {
      console.log("KYC form controller connected");
      this.setupRealtimeValidation();
      this.validateFormState();
    }

    setupRealtimeValidation() {
      // Add event listeners to all form inputs
      const form = this.element;

      // Text inputs and textareas
      form.querySelectorAll("input[type=\"text\"], input[type=\"email\"], textarea, select").forEach(input => {
        input.addEventListener("blur", (e) => this.validateField(e.target));
        input.addEventListener("input", (e) => this.clearFieldError(e.target));
      });

      // Checkboxes and radio buttons
      form.querySelectorAll("input[type=\"checkbox\"], input[type=\"radio\"]").forEach(input => {
        input.addEventListener("change", (e) => {
          this.validateField(e.target);
          this.validateFormState();
        });
      });

      // Date selects
      form.querySelectorAll("select").forEach(select => {
        select.addEventListener("change", (e) => {
          this.validateField(e.target);
          this.validateFormState();
        });
      });
    }

    togglePepSection(event) {
      const isChecked = event.target.checked;

      if (this.hasPepSectionTarget) {
        this.pepSectionTarget.style.display = isChecked ? "" : "none";

        // Clear PEP fields and their errors if unchecked
        if (!isChecked && this.hasPepFieldTarget) {
          this.pepFieldTargets.forEach(field => {
            this.clearFieldError(field);
            if (field.tagName === "INPUT" || field.tagName === "TEXTAREA") {
              field.value = "";
            } else if (field.tagName === "SELECT") {
              field.selectedIndex = 0;
            }
          });
        }
      }

      this.validateFormState();
    }

    toggleBoSection(event) {
      const isChecked = event.target.checked;

      if (this.hasBoSectionTarget) {
        this.boSectionTarget.style.display = isChecked ? "" : "none";

        // Clear BO fields and their errors if unchecked
        if (!isChecked && this.hasBoFieldTarget) {
          this.boFieldTargets.forEach(field => {
            this.clearFieldError(field);
            if (field.tagName === "INPUT" || field.tagName === "TEXTAREA") {
              field.value = "";
            } else if (field.tagName === "SELECT") {
              field.selectedIndex = 0;
            }
          });
        }
      }

      this.validateFormState();
    }

    validateField(field) {
      if (!this.isFieldVisible(field)) {
        return true;
      }

      this.clearFieldError(field);

      const wrapper = field.closest(".form-group, .mb-3, .form-check");
      const label = wrapper ? wrapper.querySelector("label") : null;
      const isRequired = field.hasAttribute("required") ||
            (label && label.textContent.includes("*"));

      if (!isRequired) {
        return true;
      }

      let isValid = true;
      let errorMessage = "";

      if (field.type === "checkbox") {
        if (!field.checked) {
          isValid = false;
          errorMessage = "Dieses Feld muss aktiviert sein";
        }
      } else if (field.type === "radio") {
        const radioGroup = this.element.querySelectorAll(`input[name="${field.name}"]`);
        const isAnyChecked = Array.from(radioGroup).some(radio => radio.checked);
        if (!isAnyChecked) {
          isValid = false;
          errorMessage = "Bitte wählen Sie eine Option";
        }
      } else if (field.tagName === "SELECT") {
        if (!field.value || field.value === "") {
          isValid = false;
          errorMessage = "Bitte wählen Sie eine Option";
        }
      } else {
        if (!field.value.trim()) {
          isValid = false;
          errorMessage = "Dieses Feld ist erforderlich";
        }
      }

      if (!isValid) {
        this.addErrorToField(field, errorMessage);
      }

      this.validateFormState();
      return isValid;
    }

    clearFieldError(field) {
      const wrapper = field.closest(".form-group, .mb-3, .form-check");
      if (wrapper) {
        wrapper.classList.remove("has-error");
        field.classList.remove("is-invalid");

        const errorDiv = wrapper.querySelector(".invalid-feedback");
        if (errorDiv) {
          errorDiv.remove();
        }
      }
    }

    validateFormState() {
      const form = this.element;
      let allValid = true;

      // Check all required fields
      const requiredInputs = form.querySelectorAll("input[required]:not([type=\"checkbox\"]):not([type=\"radio\"]), textarea[required], select[required]");
      requiredInputs.forEach(input => {
        if (!this.isFieldVisible(input)) return;

        if (input.tagName === "SELECT") {
          if (!input.value || input.value === "") {
            allValid = false;
          }
        } else {
          if (!input.value.trim()) {
            allValid = false;
          }
        }
      });

      // Check required checkboxes
      const requiredCheckboxes = form.querySelectorAll("input[type=\"checkbox\"][required]");
      requiredCheckboxes.forEach(checkbox => {
        if (!this.isFieldVisible(checkbox)) return;
        if (!checkbox.checked) {
          allValid = false;
        }
      });

      // Check required radio groups
      const radioGroups = this.getRequiredRadioGroups(form);
      radioGroups.forEach(groupName => {
        const radios = form.querySelectorAll(`input[type="radio"][name="${groupName}"]`);
        const firstRadio = radios[0];

        if (!this.isFieldVisible(firstRadio)) return;

        const isChecked = Array.from(radios).some(radio => radio.checked);
        if (!isChecked) {
          allValid = false;
        }
      });

      // Check PEP fields if PEP checkbox is checked
      if (this.hasPepCheckboxTarget && this.pepCheckboxTarget.checked && this.hasPepFieldTarget) {
        this.pepFieldTargets.forEach(field => {
          if (!this.isFieldVisible(field)) return;

          const wrapper = field.closest(".form-group, .mb-3");
          const label = wrapper ? wrapper.querySelector("label") : null;
          const isRequired = field.hasAttribute("required") ||
                    (label && label.textContent.includes("*"));

          if (!isRequired) return;

          if (field.tagName === "SELECT") {
            if (!field.value || field.value === "") allValid = false;
          } else if (field.tagName === "INPUT" || field.tagName === "TEXTAREA") {
            if (!field.value.trim()) allValid = false;
          }
        });
      }

      // Check BO fields if BO checkbox is checked
      if (this.hasBoCheckboxTarget && this.boCheckboxTarget.checked && this.hasBoFieldTarget) {
        this.boFieldTargets.forEach(field => {
          if (!this.isFieldVisible(field)) return;

          const wrapper = field.closest(".form-group, .mb-3");
          const label = wrapper ? wrapper.querySelector("label") : null;
          const isRequired = field.hasAttribute("required") ||
                    (label && label.textContent.includes("*"));

          if (!isRequired) return;

          if (field.tagName === "SELECT") {
            if (!field.value || field.value === "") allValid = false;
          } else if (field.tagName === "INPUT" || field.tagName === "TEXTAREA") {
            if (!field.value.trim()) allValid = false;
          }
        });
      }

      // Update submit button state
      this.updateSubmitButton(allValid);

      // Remove error summary if form is valid
      if (allValid) {
        const existingSummary = form.querySelector(".alert-danger.kyc-error-summary");
        if (existingSummary) {
          existingSummary.remove();
        }
      }

      return allValid;
    }

    updateSubmitButton(isValid) {
      if (this.hasSubmitButtonTarget) {
        if (isValid) {
          this.submitButtonTarget.disabled = false;
          this.submitButtonTarget.classList.remove("btn-secondary");
          this.submitButtonTarget.classList.add("btn-primary");
        } else {
          this.submitButtonTarget.disabled = true;
          this.submitButtonTarget.classList.remove("btn-primary");
          this.submitButtonTarget.classList.add("btn-secondary");
        }
      }
    }

    validate(event) {
      const form = event.target;
      let isValid = true;
      let errorMessages = [];

      // Remove previous error messages
      this.removeErrorMessages(form);

      // Validate required text inputs
      const requiredInputs = form.querySelectorAll("input[required]:not([type=\"checkbox\"]):not([type=\"radio\"]), textarea[required]");
      requiredInputs.forEach(input => {
        if (!this.isFieldVisible(input)) return;

        if (!input.value.trim()) {
          isValid = false;
          this.addErrorToField(input, "Dieses Feld ist erforderlich");
          errorMessages.push(this.getFieldLabel(input));
        }
      });

      // Validate required checkboxes
      const requiredCheckboxes = form.querySelectorAll("input[type=\"checkbox\"][required]");
      requiredCheckboxes.forEach(checkbox => {
        if (!this.isFieldVisible(checkbox)) return;

        if (!checkbox.checked) {
          isValid = false;
          this.addErrorToField(checkbox, "Dieses Feld muss aktiviert sein");
          errorMessages.push(this.getFieldLabel(checkbox));
        }
      });

      // Validate required radio buttons
      const radioGroups = this.getRequiredRadioGroups(form);
      radioGroups.forEach(groupName => {
        const radios = form.querySelectorAll(`input[type="radio"][name="${groupName}"]`);
        const firstRadio = radios[0];

        if (!this.isFieldVisible(firstRadio)) return;

        const isChecked = Array.from(radios).some(radio => radio.checked);
        if (!isChecked) {
          isValid = false;
          this.addErrorToField(firstRadio, "Bitte wählen Sie eine Option");
          errorMessages.push(this.getFieldLabel(firstRadio));
        }
      });

      // Validate required selects (including date selects)
      const requiredSelects = form.querySelectorAll("select[required]");
      requiredSelects.forEach(select => {
        if (!this.isFieldVisible(select)) return;

        if (!select.value || select.value === "") {
          isValid = false;
          this.addErrorToField(select, "Bitte wählen Sie eine Option");
          errorMessages.push(this.getFieldLabel(select));
        }
      });

      // Validate PEP fields if PEP checkbox is checked
      if (this.hasPepCheckboxTarget && this.pepCheckboxTarget.checked) {
        isValid = this.validateConditionalSection(
          this.pepFieldTargets,
          isValid,
          errorMessages,
          form
        );
      }

      // Validate BO fields if BO checkbox is checked
      if (this.hasBoCheckboxTarget && this.boCheckboxTarget.checked) {
        isValid = this.validateConditionalSection(
          this.boFieldTargets,
          isValid,
          errorMessages,
          form
        );
      }

      if (!isValid) {
        event.preventDefault();

        // Show summary error message
        this.showErrorSummary(form, errorMessages);

        // Scroll to first error
        const firstError = form.querySelector(".field_with_errors, .is-invalid");
        if (firstError) {
          firstError.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      }

      return isValid;
    }

    validateConditionalSection(fields, isValid, errorMessages) {
      let sectionValid = isValid;

      fields.forEach(field => {
        if (!this.isFieldVisible(field)) return;

        const wrapper = field.closest(".form-group, .mb-3");
        const label = wrapper ? wrapper.querySelector("label") : null;
        const isRequired = field.hasAttribute("required") ||
                (label && label.textContent.includes("*"));

        if (!isRequired) return;

        if (field.tagName === "SELECT") {
          if (!field.value || field.value === "") {
            sectionValid = false;
            this.addErrorToField(field, "Bitte wählen Sie eine Option");
            errorMessages.push(this.getFieldLabel(field));
          }
        } else if (field.tagName === "INPUT" || field.tagName === "TEXTAREA") {
          if (!field.value.trim()) {
            sectionValid = false;
            this.addErrorToField(field, "Dieses Feld ist erforderlich");
            errorMessages.push(this.getFieldLabel(field));
          }
        }
      });

      return sectionValid;
    }

    isFieldVisible(field) {
      let element = field;
      while (element) {
        const style = window.getComputedStyle(element);
        if (style.display === "none" || style.visibility === "hidden") {
          return false;
        }
        element = element.parentElement;
      }
      return true;
    }

    getRequiredRadioGroups(form) {
      const radioInputs = form.querySelectorAll("input[type=\"radio\"][required]");
      const groups = new Set();
      radioInputs.forEach(radio => {
        if (radio.name && this.isFieldVisible(radio)) {
          groups.add(radio.name);
        }
      });
      return Array.from(groups);
    }

    addErrorToField(field, message) {
      const wrapper = field.closest(".form-group, .mb-3, .form-check");
      if (wrapper) {
        wrapper.classList.add("has-error");

        // Add error class to field
        field.classList.add("is-invalid");

        // Add error message
        const errorDiv = document.createElement("div");
        errorDiv.className = "invalid-feedback d-block";
        errorDiv.textContent = message;

        // Insert after field or after label for checkboxes
        if (field.type === "checkbox" || field.type === "radio") {
          const label = wrapper.querySelector("label");
          if (label) {
            label.after(errorDiv);
          } else {
            field.after(errorDiv);
          }
        } else {
          field.after(errorDiv);
        }
      }
    }

    removeErrorMessages(form) {
      // Remove error classes
      form.querySelectorAll(".has-error").forEach(el => {
        el.classList.remove("has-error");
      });
      form.querySelectorAll(".is-invalid").forEach(el => {
        el.classList.remove("is-invalid");
      });

      // Remove error messages
      form.querySelectorAll(".invalid-feedback").forEach(el => {
        el.remove();
      });

      // Remove summary
      const existingSummary = form.querySelector(".alert-danger.kyc-error-summary");
      if (existingSummary) {
        existingSummary.remove();
      }
    }

    showErrorSummary(form, errorMessages) {
      const uniqueMessages = [...new Set(errorMessages)];

      const alertDiv = document.createElement("div");
      alertDiv.className = "alert alert-danger kyc-error-summary";
      alertDiv.setAttribute("role", "alert");

      const title = document.createElement("h5");
      title.textContent = "Bitte korrigieren Sie folgende Fehler:";
      alertDiv.appendChild(title);

      if (uniqueMessages.length > 0) {
        const ul = document.createElement("ul");
        ul.className = "mb-0";
        uniqueMessages.slice(0, 10).forEach(msg => {
          const li = document.createElement("li");
          li.textContent = msg;
          ul.appendChild(li);
        });
        alertDiv.appendChild(ul);

        if (uniqueMessages.length > 10) {
          const more = document.createElement("p");
          more.className = "mb-0 mt-2";
          more.textContent = `... und ${uniqueMessages.length - 10} weitere Fehler`;
          alertDiv.appendChild(more);
        }
      }

      // Insert at top of form
      form.insertBefore(alertDiv, form.firstChild);
    }

    getFieldLabel(field) {
      const wrapper = field.closest(".form-group, .mb-3, .form-check");
      if (wrapper) {
        const label = wrapper.querySelector("label");
        if (label) {
          return label.textContent.replace("*", "").trim();
        }
      }
      return field.name || "Unbekanntes Feld";
    }
}
